/**
 * Country Detail Page - Full country information with universities
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Loader2, ArrowLeft, GraduationCap, MapPin, DollarSign, Shield,
  Briefcase, Globe, TrendingUp, Heart, Home, Thermometer,
  Check, Building2, Award, Users, Calendar
} from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { countryAPI } from '../api/country-api';
import { SEO, Header } from '../../../shared/components';
import type { CountryWithUniversities } from '../types';

const CountryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [country, setCountry] = useState<CountryWithUniversities | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const backUrl = (location.state as any)?.from || '/explore/countries';

  useEffect(() => {
    const loadCountry = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const countryData = await countryAPI.getCountryById(parseInt(id));
        setCountry(countryData);
      } catch (err) {
        console.error('Failed to load country:', err);
        setError('Failed to load country details');
      } finally {
        setIsLoading(false);
      }
    };

    loadCountry();
  }, [id]);

  if (isLoading) {
    return (
      <>
        <SEO title="Loading Country..." />
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-success" />
            <span className="text-foreground font-medium">Loading country details...</span>
          </div>
        </div>
      </>
    );
  }

  if (error || !country) {
    return (
      <>
        <SEO title="Country Not Found" />
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <p className="mb-4 text-destructive text-lg">{error || 'Country not found'}</p>
            <Button onClick={() => navigate(backUrl)} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Countries
            </Button>
          </div>
        </div>
      </>
    );
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return `$${(amount / 1000).toFixed(0)}k`;
  };

  return (
    <>
      <SEO
        title={`Study in ${country.name} - Universities, Costs & Visa Info`}
        description={`Explore study opportunities in ${country.name}. ${country.universities_count} universities, safety index ${country.safety_index}, living costs and visa policies.`}
      />

      <Header />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-success via-success to-emerald-700 text-white py-12">
          <div className="container mx-auto px-6">
            {/* Back Button */}
            <button
              onClick={() => navigate(backUrl)}
              className="mb-6 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl border border-white/20 transition-all"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>

            <div className="max-w-4xl">
              {/* Country Name */}
              <div className="flex items-center gap-4 mb-6">
                {country.flag_emoji && (
                  <span className="text-6xl leading-none">{country.flag_emoji}</span>
                )}
                <div>
                  <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
                    Study in {country.name}
                  </h1>
                  {country.region && (
                    <p className="text-xl text-white/90 mt-2 flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      {country.region}
                    </p>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20">
                  <div className="text-sm text-white/70 mb-1">Universities</div>
                  <div className="text-2xl font-bold">{country.universities_count}</div>
                </div>

                {country.safety_index && (
                  <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/70 mb-1">Safety Index</div>
                    <div className="text-2xl font-bold">{country.safety_index.toFixed(1)}/100</div>
                  </div>
                )}

                {country.avg_living_cost_usd_yearly && (
                  <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/70 mb-1">Living Cost/Year</div>
                    <div className="text-2xl font-bold">{formatCurrency(country.avg_living_cost_usd_yearly)}</div>
                  </div>
                )}

                {country.intl_students_count && (
                  <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20">
                    <div className="text-sm text-white/70 mb-1">Int'l Students</div>
                    <div className="text-2xl font-bold">{(country.intl_students_count / 1000).toFixed(0)}k+</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-6xl">
            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {/* Left Column - Main Info */}
              <div className="lg:col-span-2 space-y-8">
                {/* Costs */}
                {(country.avg_tuition_undergrad_usd_min || country.avg_living_cost_usd_yearly) && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                      <DollarSign className="h-6 w-6 text-success" />
                      Cost of Study
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {country.avg_tuition_undergrad_usd_min && (
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Tuition Range (Undergrad)</div>
                          <div className="font-bold text-foreground text-lg">
                            {formatCurrency(country.avg_tuition_undergrad_usd_min)} - {formatCurrency(country.avg_tuition_undergrad_usd_max)}
                          </div>
                        </div>
                      )}
                      {country.avg_living_cost_usd_yearly && (
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Living Cost per Year</div>
                          <div className="font-bold text-foreground text-lg">
                            {formatCurrency(country.avg_living_cost_usd_yearly)}
                          </div>
                        </div>
                      )}
                      {country.currency && (
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Currency</div>
                          <div className="font-semibold text-foreground">{country.currency}</div>
                        </div>
                      )}
                      {country.cost_of_living_index && (
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Cost of Living Index</div>
                          <div className="font-semibold text-foreground">{country.cost_of_living_index.toFixed(1)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Work & Immigration */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                    <Briefcase className="h-6 w-6 text-success" />
                    Work & Immigration
                  </h2>
                  <div className="space-y-4">
                    {country.part_time_work_allowed && (
                      <div className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-success mt-0.5" />
                        <div>
                          <div className="font-semibold text-foreground">Part-time Work Allowed</div>
                          {country.part_time_hours_per_week && (
                            <div className="text-muted-foreground">Up to {country.part_time_hours_per_week} hours per week</div>
                          )}
                        </div>
                      </div>
                    )}
                    {country.post_study_work_visa_months && country.post_study_work_visa_months > 0 && (
                      <div className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-success mt-0.5" />
                        <div>
                          <div className="font-semibold text-foreground">Post-Study Work Visa</div>
                          <div className="text-muted-foreground">{country.post_study_work_visa_months} months work rights after graduation</div>
                        </div>
                      </div>
                    )}
                    {country.permanent_residency_possible && (
                      <div className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-success mt-0.5" />
                        <div>
                          <div className="font-semibold text-foreground">Permanent Residency Pathway</div>
                          {country.pr_pathway_description && (
                            <div className="text-muted-foreground mt-1">{country.pr_pathway_description}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Universities */}
                {country.universities && country.universities.length > 0 && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                      <GraduationCap className="h-6 w-6 text-success" />
                      Top Universities ({country.universities.length})
                    </h2>
                    <div className="space-y-3">
                      {country.universities.slice(0, 10).map((university) => (
                        <div
                          key={university.id}
                          onClick={() => navigate(`/university/${university.slug || university.id}`)}
                          className="flex items-start justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors cursor-pointer group"
                        >
                          <div>
                            <h3 className="font-semibold text-foreground group-hover:text-success transition-colors">
                              {university.name}
                            </h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              {university.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  {university.city}
                                </span>
                              )}
                              {university.programs_count > 0 && (
                                <span>{university.programs_count} programs</span>
                              )}
                            </div>
                          </div>
                          {university.qs_world_ranking && (
                            <div className="text-right">
                              <div className="text-xs text-muted-foreground">QS Ranking</div>
                              <div className="text-lg font-bold text-foreground">#{university.qs_world_ranking}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {country.universities.length > 10 && (
                      <Button
                        onClick={() => navigate('/explore/universities', {
                          state: { filters: { country: country.name } }
                        })}
                        variant="outline"
                        className="w-full mt-4"
                      >
                        View All {country.universities.length} Universities
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column - Additional Info */}
              <div className="space-y-6">
                {/* Quality of Life */}
                {(country.safety_index || country.healthcare_quality_score || country.quality_of_life_score) && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <Heart className="h-5 w-5 text-success" />
                      Quality of Life
                    </h3>
                    <div className="space-y-3">
                      {country.safety_index && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Safety Index</span>
                          <span className="font-semibold text-foreground">{country.safety_index.toFixed(1)}/100</span>
                        </div>
                      )}
                      {country.healthcare_quality_score && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Healthcare Quality</span>
                          <span className="font-semibold text-foreground">{country.healthcare_quality_score.toFixed(1)}/10</span>
                        </div>
                      )}
                      {country.quality_of_life_score && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Overall Quality</span>
                          <span className="font-semibold text-foreground">{country.quality_of_life_score.toFixed(1)}/10</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Climate */}
                {(country.climate_type || country.avg_temp_summer_c || country.avg_temp_winter_c) && (
                  <div className="bg-card rounded-2xl p-6 border border-border">
                    <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <Thermometer className="h-5 w-5 text-success" />
                      Climate
                    </h3>
                    <div className="space-y-3">
                      {country.climate_type && (
                        <div>
                          <span className="text-sm text-muted-foreground">Climate Type</span>
                          <div className="font-semibold text-foreground mt-1">{country.climate_type}</div>
                        </div>
                      )}
                      {country.avg_temp_summer_c && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Summer Average</span>
                          <span className="font-semibold text-foreground">{country.avg_temp_summer_c}°C</span>
                        </div>
                      )}
                      {country.avg_temp_winter_c && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Winter Average</span>
                          <span className="font-semibold text-foreground">{country.avg_temp_winter_c}°C</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Language & Tests */}
                <div className="bg-card rounded-2xl p-6 border border-border">
                  <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-success" />
                    Language & Requirements
                  </h3>
                  <div className="space-y-3">
                    {country.primary_language && (
                      <div>
                        <span className="text-sm text-muted-foreground">Primary Language</span>
                        <div className="font-semibold text-foreground mt-1">{country.primary_language}</div>
                      </div>
                    )}
                    {country.english_tests_required && country.english_tests_required.length > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">English Tests Accepted</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {country.english_tests_required.map((test, idx) => (
                            <span key={idx} className="px-3 py-1 bg-muted text-foreground rounded-full text-xs font-medium">
                              {test}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {country.standardized_tests_accepted && country.standardized_tests_accepted.length > 0 && (
                      <div>
                        <span className="text-sm text-muted-foreground">Standardized Tests</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {country.standardized_tests_accepted.map((test, idx) => (
                            <span key={idx} className="px-3 py-1 bg-muted text-foreground rounded-full text-xs font-medium">
                              {test}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate('/explore/universities', {
                  state: { filters: { country: country.name } }
                })}
                className="bg-success hover:bg-success/90"
              >
                Browse Universities in {country.name}
              </Button>
              <Button
                onClick={() => navigate('/explore/programs', {
                  state: { filters: { country: country.name } }
                })}
                variant="outline"
              >
                Browse Programs
              </Button>
              <Button
                onClick={() => navigate('/quiz')}
                variant="outline"
              >
                Take Quiz for Match
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountryDetailPage;
